/**
 * Design tab — right rail, canvas markers, and inspector for the visual
 * editor's design mode. Extracted from design-studio-page.tsx.
 *
 * Exports:
 * - `StudioElement`, `Proposal` — types for the parent page state
 * - `StudioMarker` — draggable canvas marker (glyph-shaped)
 * - `DesignRail` — right panel (add markings, Alan, marking list, lifecycle)
 * - `DesignInspector` — below-canvas inspector for the selected element
 */
import { useState } from "react";
import { Check, ImageOff, Loader2, Paintbrush, Plus, Sparkles, Trash2, X } from "lucide-react";
import { Button } from "@flint/ui";
import { FLAT_THEME_VARS } from "../design-studio/garment-flat.tsx";
import { ElementGlyph, glyphBox } from "../design-studio/element-glyphs.tsx";
import {
  ELEMENT_META,
  ELEMENT_TYPES,
  cmTextToMm,
  elementLabel,
  mmToCmText,
  type StudioElementType,
} from "../design-studio/element-meta.js";
import { clamp } from "./draggable-pin.tsx";

/* ── Re-exports the parent page needs ─────────────────────────────────── */

export { ELEMENT_META, glyphBox, type StudioElementType };

/* ── Types ────────────────────────────────────────────────────────────── */

export interface StudioElement {
  key: string;
  elementType: StudioElementType;
  view: "front" | "back";
  x: number;
  y: number;
  widthMm: number | null;
  heightMm: number | null;
  offsetNote: string | null;
  note: string | null;
  artworkObjectKey: string | null;
  /** Display-only — resolved by the router, not serialized to wire on save. */
  artworkUrl: string | null;
  colourway: string | null;
}

export interface Proposal extends Omit<StudioElement, "key"> {
  rationale: string | null;
}

/* ── StudioMarker (canvas) ────────────────────────────────────────────── */

export function StudioMarker({
  element,
  selected,
  readOnly,
  stageRef,
  onSelect,
  onPlace,
}: {
  element: StudioElement;
  selected: boolean;
  readOnly: boolean;
  stageRef: React.RefObject<HTMLDivElement | null>;
  onSelect: () => void;
  onPlace: (x: number, y: number) => void;
}) {
  const [drag, setDrag] = useState<{
    x: number;
    y: number;
    moved: boolean;
    sx: number;
    sy: number;
  } | null>(null);
  const x = drag?.x ?? element.x;
  const y = drag?.y ?? element.y;
  const meta = ELEMENT_META[element.elementType];
  const box = glyphBox(element.elementType, element.widthMm, element.heightMm);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${elementLabel(element.elementType)} marking`}
      className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-sm ${
        readOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing"
      } ${selected ? "outline outline-2 outline-offset-4 outline-sky-500/80" : ""}`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${box.width / 4}%`,
        height: `${box.height / 5}%`,
      }}
      onPointerDown={(e) => {
        if (readOnly) return;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* older browsers */
        }
        setDrag({ x, y, moved: false, sx: e.clientX, sy: e.clientY });
      }}
      onPointerMove={(e) => {
        if (!drag) return;
        const rect = stageRef.current?.getBoundingClientRect();
        if (!rect) return;
        const nx = clamp(((e.clientX - rect.left) / rect.width) * 100);
        const ny = clamp(((e.clientY - rect.top) / rect.height) * 100);
        setDrag({
          ...drag,
          x: nx,
          y: ny,
          moved:
            drag.moved ||
            Math.abs(e.clientX - drag.sx) > 3 ||
            Math.abs(e.clientY - drag.sy) > 3,
        });
      }}
      onPointerUp={(e) => {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* older browsers */
        }
        const current = drag;
        setDrag(null);
        if (current && current.moved) onPlace(current.x, current.y);
        else onSelect();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect();
      }}
    >
      {element.artworkUrl ? (
        <ArtworkOverlay url={element.artworkUrl} />
      ) : (
        <svg
          viewBox={`${-box.width / 2} ${-box.height / 2} ${box.width} ${box.height}`}
          className="pointer-events-none h-full w-full overflow-visible"
          style={FLAT_THEME_VARS}
          aria-hidden
        >
          <ElementGlyph type={element.elementType} box={box} />
        </svg>
      )}
      <span
        className={`pointer-events-none absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full px-0.5 text-xs font-bold shadow-sm ${meta?.chip ?? "bg-line text-ink-2"}`}
      >
        {meta?.code ?? "?"}
      </span>
    </div>
  );
}

/** Artwork thumbnail overlay on a canvas marker — falls back to glyph on error. */
function ArtworkOverlay({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={url}
      alt=""
      className="pointer-events-none h-full w-full rounded-sm object-contain"
      onError={() => setFailed(true)}
    />
  );
}

/* ── ProposalGhost (canvas) ───────────────────────────────────────────── */

export function ProposalGhost({
  proposal,
  index,
}: {
  proposal: Proposal;
  index: number;
}) {
  const box = glyphBox(proposal.elementType, proposal.widthMm, proposal.heightMm);
  return (
    <div
      key={`proposal-${index}`}
      className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-sm outline-dashed outline-2 outline-offset-4 outline-sky-400/80"
      style={{
        left: `${proposal.x}%`,
        top: `${proposal.y}%`,
        width: `${box.width / 4}%`,
        height: `${box.height / 5}%`,
      }}
    >
      <svg
        viewBox={`${-box.width / 2} ${-box.height / 2} ${box.width} ${box.height}`}
        className="h-full w-full overflow-visible opacity-60"
        style={FLAT_THEME_VARS}
        aria-hidden
      >
        <ElementGlyph type={proposal.elementType} box={box} />
      </svg>
      <span className="absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-sky-600 px-0.5 text-xs font-bold text-white">
        {ELEMENT_META[proposal.elementType]?.code ?? "?"}
      </span>
    </div>
  );
}

/* ── DesignRail (right panel) ─────────────────────────────────────────── */

export function DesignRail({
  readOnly,
  elements,
  selectedKey,
  dirty,
  currentVersion,
  archived,
  prompt,
  proposals,
  proposePending,
  approvePending,
  onAddElement,
  onSelectElement,
  onSetView,
  onPromptChange,
  onPropose,
  onAcceptProposal,
  onDiscardProposal,
  onApproveOpen,
}: {
  readOnly: boolean;
  elements: StudioElement[];
  selectedKey: string | null;
  dirty: boolean;
  currentVersion: number;
  archived: boolean;
  prompt: string;
  proposals: Proposal[];
  proposePending: boolean;
  approvePending: boolean;
  onAddElement: (type: StudioElementType) => void;
  onSelectElement: (key: string) => void;
  onSetView: (view: "front" | "back") => void;
  onPromptChange: (prompt: string) => void;
  onPropose: () => void;
  onAcceptProposal: (index: number) => void;
  onDiscardProposal: (index: number) => void;
  onApproveOpen: () => void;
}) {
  return (
    <>
      {/* Add markings */}
      {!readOnly ? (
        <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">
            Add a marking
          </h4>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {ELEMENT_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => onAddElement(type)}
                className="inline-flex items-center gap-1 rounded-md border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-2 transition hover:border-line-2 hover:bg-raised"
              >
                <Plus size={11} aria-hidden />
                {ELEMENT_META[type].label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Alan */}
      {!readOnly ? (
        <div className="rounded-md border border-sky-100 bg-sky-50/50 p-3.5 shadow-sm">
          <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-sky-700">
            <Sparkles size={13} aria-hidden />
            Tell Alan
          </h4>
          <div className="mt-2.5 flex gap-2">
            <input
              value={prompt}
              onChange={(e) => onPromptChange(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  prompt.trim().length >= 3 &&
                  !proposePending
                ) {
                  onPropose();
                }
              }}
              placeholder={`"logo on left chest, 9.7 by 10.2 cm"`}
              maxLength={600}
              className="min-w-0 flex-1 rounded-md border border-line bg-paper px-2.5 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
            />
            <Button
              size="sm"
              disabled={prompt.trim().length < 3 || proposePending}
              onClick={onPropose}
            >
              {proposePending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                "Propose"
              )}
            </Button>
          </div>
          {proposals.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-2">
              {proposals.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-md border border-dashed border-sky-200 bg-paper p-2"
                >
                  <span
                    className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${ELEMENT_META[p.elementType]?.chip ?? "bg-line text-ink-2"}`}
                  >
                    {ELEMENT_META[p.elementType]?.code ?? "?"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-ink">
                      {elementLabel(p.elementType)} · {p.view}
                    </div>
                    <div className="truncate text-xs text-muted">
                      {p.offsetNote ?? p.rationale ?? "Proposed placement"}
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label={`Add proposal ${i + 1}`}
                    onClick={() => onAcceptProposal(i)}
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-ink text-paper transition hover:bg-ink-2"
                  >
                    <Check size={13} aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-label={`Discard proposal ${i + 1}`}
                    onClick={() => onDiscardProposal(i)}
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-muted transition hover:bg-bad-50 hover:text-bad"
                  >
                    <X size={13} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-muted">
              Proposals appear here — nothing lands on the flat until you accept
              it.
            </p>
          )}
        </div>
      ) : null}

      {/* Marking list */}
      <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">
          Markings · v{currentVersion}
          {dirty ? (
            <span className="ml-1.5 normal-case text-warn">
              (unsaved changes)
            </span>
          ) : null}
        </h4>
        {elements.length === 0 ? (
          <p className="mt-2 text-sm text-muted">
            No markings yet — add one, or describe the design to Alan.
          </p>
        ) : (
          <ul className="mt-2 flex flex-col">
            {elements.map((element) => (
              <li key={element.key}>
                <button
                  type="button"
                  onClick={() => {
                    onSetView(element.view);
                    onSelectElement(element.key);
                  }}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-raised ${
                    element.key === selectedKey ? "bg-sky-50" : ""
                  }`}
                >
                  <span
                    className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${ELEMENT_META[element.elementType]?.chip}`}
                  >
                    {ELEMENT_META[element.elementType]?.code}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-ink">
                      {elementLabel(element.elementType)}
                      <span className="ml-1 text-muted">
                        · {element.view}
                      </span>
                    </span>
                    {element.offsetNote ? (
                      <span className="block truncate text-xs text-muted">
                        {element.offsetNote}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Lifecycle */}
      <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">
          Lifecycle
        </h4>
        <div className="mt-2.5 flex flex-col gap-2">
          {!archived ? (
            <>
              <Button
                intent="ghost"
                size="sm"
                className="justify-start border border-line bg-paper"
                disabled={approvePending || dirty}
                onClick={onApproveOpen}
              >
                <Check size={14} />
                Note buyer sign-off for v{currentVersion}
              </Button>
              {dirty ? (
                <p className="text-xs text-muted">
                  Save your changes first.
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted">
              This style is cancelled or closed — the design is read-only
              history.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

/* ── DesignInspector (below canvas) ───────────────────────────────────── */

export function DesignInspector({
  selected,
  readOnly,
  palette,
  artworkUpload,
  onUpdate,
  onRemove,
}: {
  selected: StudioElement | null;
  readOnly: boolean;
  /** Colour palette sourced from the style's ratio grid. */
  palette: Array<{ colour: string; colourHex: string | null }>;
  /** Render slot for artwork upload button + status, owned by parent via useS3Upload. */
  artworkUpload?: React.ReactNode;
  onUpdate: (key: string, patch: Partial<StudioElement>) => void;
  onRemove: (key: string) => void;
}) {
  if (!selected || readOnly) return null;

  return (
    <div className="rounded-md border border-line bg-paper p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">
          {elementLabel(selected.elementType)}
          <span className="ml-2 font-mono text-xs text-muted">
            {selected.x.toFixed(0)}%, {selected.y.toFixed(0)}%
          </span>
        </h3>
        <button
          type="button"
          onClick={() => onRemove(selected.key)}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-bad transition hover:bg-bad-50"
        >
          <Trash2 size={13} aria-hidden />
          Remove
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Width (cm)</span>
          <input
            inputMode="decimal"
            defaultValue={mmToCmText(selected.widthMm)}
            key={`w-${selected.key}`}
            onBlur={(e) =>
              onUpdate(selected.key, {
                widthMm: cmTextToMm(e.currentTarget.value),
              })
            }
            placeholder="e.g. 9.7"
            className="rounded-md border border-line bg-paper px-2.5 py-1.5 font-mono text-sm focus:border-sky-400 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Height (cm)</span>
          <input
            inputMode="decimal"
            defaultValue={mmToCmText(selected.heightMm)}
            key={`h-${selected.key}`}
            onBlur={(e) =>
              onUpdate(selected.key, {
                heightMm: cmTextToMm(e.currentTarget.value),
              })
            }
            placeholder="e.g. 10.2"
            className="rounded-md border border-line bg-paper px-2.5 py-1.5 font-mono text-sm focus:border-sky-400 focus:outline-none"
          />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">
            Placement note (rendered verbatim on the tech pack)
          </span>
          <input
            value={selected.offsetNote ?? ""}
            onChange={(e) =>
              onUpdate(selected.key, {
                offsetNote:
                  e.currentTarget.value === "" ? null : e.currentTarget.value,
              })
            }
            placeholder="e.g. 5/8 below the neck line"
            maxLength={160}
            className="rounded-md border border-line bg-paper px-2.5 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
          />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Note</span>
          <input
            value={selected.note ?? ""}
            onChange={(e) =>
              onUpdate(selected.key, {
                note:
                  e.currentTarget.value === "" ? null : e.currentTarget.value,
              })
            }
            placeholder="e.g. BTN DTM, 4-hole"
            maxLength={500}
            className="rounded-md border border-line bg-paper px-2.5 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
          />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Colourway</span>
          {palette.length > 0 ? (
            <select
              value={selected.colourway ?? ""}
              onChange={(e) =>
                onUpdate(selected.key, {
                  colourway:
                    e.currentTarget.value === ""
                      ? null
                      : e.currentTarget.value,
                })
              }
              className="rounded-md border border-line bg-paper px-2.5 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
            >
              <option value="">None</option>
              {palette.map((c) => (
                <option key={c.colour} value={c.colour}>
                  {c.colour}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-muted">
              No colours in this style's ratio grid
            </span>
          )}
          {selected.colourway && palette.length > 0 ? (
            <span className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted">
              <span
                className="inline-block h-3 w-3 rounded-full border border-line"
                style={{
                  backgroundColor:
                    palette.find((c) => c.colour === selected.colourway)
                      ?.colourHex ?? "#ccc",
                }}
              />
              {selected.colourway}
            </span>
          ) : null}
        </label>
        <div className="col-span-2 flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted">Artwork</span>
          {selected.artworkUrl ? (
            <div className="flex items-start gap-3">
              <img
                src={selected.artworkUrl}
                alt="Artwork preview"
                className="max-h-32 rounded-md border border-line object-contain"
              />
              <button
                type="button"
                onClick={() =>
                  onUpdate(selected.key, {
                    artworkObjectKey: null,
                    artworkUrl: null,
                  })
                }
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-bad transition hover:bg-bad-50"
              >
                <ImageOff size={13} aria-hidden />
                Remove
              </button>
            </div>
          ) : null}
          {artworkUpload}
        </div>
      </div>
    </div>
  );
}

/* ── PartColourPicker (below canvas, when a garment part is selected) ── */

export function PartColourPicker({
  selectedPart,
  palette,
  currentHex,
  onPick,
}: {
  selectedPart: string;
  palette: Array<{ colour: string; colourHex: string | null }>;
  /** Current hex assigned to this part, if any. */
  currentHex: string | null;
  /** Called with hex when a colour is picked, or null to clear. */
  onPick: (hex: string | null) => void;
}) {
  const partLabel = selectedPart
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="rounded-md border border-sky-200 bg-sky-50/50 p-3.5 shadow-sm">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-sky-700">
        <Paintbrush size={13} aria-hidden />
        Tint · {partLabel}
      </h4>
      {palette.length > 0 ? (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {palette.map((c) => {
            const hex = c.colourHex ?? "#ccc";
            const active = currentHex === hex;
            return (
              <button
                key={c.colour}
                type="button"
                title={c.colour}
                onClick={() => onPick(active ? null : hex)}
                className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition ${
                  active
                    ? "border-sky-500 bg-sky-100 text-sky-800"
                    : "border-line bg-paper text-ink-2 hover:border-line-2 hover:bg-raised"
                }`}
              >
                <span
                  className="inline-block h-3.5 w-3.5 rounded-full border border-line/50"
                  style={{ backgroundColor: hex }}
                />
                {c.colour}
              </button>
            );
          })}
          {currentHex ? (
            <button
              type="button"
              onClick={() => onPick(null)}
              className="inline-flex items-center gap-1 rounded-md border border-line bg-paper px-2 py-1 text-xs font-medium text-muted transition hover:bg-raised"
            >
              <X size={11} aria-hidden />
              Clear
            </button>
          ) : null}
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted">
          No colours in this style's ratio grid — add colours in the style
          composer to enable tinting.
        </p>
      )}
    </div>
  );
}
